import { IButtonOptions } from "devextreme-react/button";
import { ButtonItem, Form, FormRef, SimpleItem } from "devextreme-react/form";
import { Popup } from "devextreme-react/popup";
import { ITextAreaOptions } from "devextreme-react/text-area";
import notify from "../../lib/notify";
import { FormEvent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { realizeDocumentSendRequest } from "../../api/document-send-requests";
import { DocumentSendRequestWithNames } from "../../types";

type DocumentSendRequestRealizePopupProps = {
  documentSendRequest: DocumentSendRequestWithNames;
  handleCompleted: () => void;
  handleClose: () => void;
};

export default function DocumentSendRequestRealizePopup({
  documentSendRequest,
  handleCompleted,
  handleClose,
}: DocumentSendRequestRealizePopupProps) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(null);
  const formData = useRef({
    comment: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsLoading(true);
      realizeDocumentSendRequest(
        documentSendRequest.id,
        formData.current.comment === "" ? null : formData.current.comment
      )
        .then(handleCompleted)
        .catch((error) => notify(error.message, "error", 5000))
        .finally(() => setIsLoading(false));
    },
    [documentSendRequest.id, handleCompleted]
  );

  return (
    <Popup
      title={t("documentSendRequests.realizePopup.title")}
      maxWidth={500}
      height="auto"
      maxHeight="80vh"
      visible
      showCloseButton
      dragEnabled
      onHiding={handleClose}
      disabled={isLoading}
    >
      <div className="flex flex-col gap-3">
        <div>
          <div className="dx-field-item-label dx-field-item-label-location-top">
            <span className="dx-field-item-label-content">
              <span className="dx-field-item-label-text">
                {t("documentSendRequests.realizePopup.employee")}:
              </span>
            </span>
          </div>
          <div>{documentSendRequest.applicant}</div>
        </div>
        <div>
          <div className="dx-field-item-label dx-field-item-label-location-top">
            <span className="dx-field-item-label-content">
              <span className="dx-field-item-label-text">
                {t("documentSendRequests.realizePopup.employeeEmailAddress")}:
              </span>
            </span>
          </div>
          <div>{documentSendRequest.applicantEmailAddress}</div>
        </div>
        <div>
          <div className="dx-field-item-label dx-field-item-label-location-top">
            <span className="dx-field-item-label-content">
              <span className="dx-field-item-label-text">
                {t("documentSendRequests.realizePopup.document")}:
              </span>
            </span>
          </div>
          <div>{documentSendRequest.documentTypeWithDate}</div>
        </div>
        <form onSubmit={handleSubmit}>
          <Form ref={formRef} formData={formData.current}>
            <SimpleItem
              dataField="comment"
              label={{
                text: t("documentSendRequests.realizePopup.comment"),
              }}
              editorType="dxTextArea"
              editorOptions={
                {
                  height: 76,
                  maxLength: 300,
                } as ITextAreaOptions
              }
            />
            <ButtonItem
              buttonOptions={
                {
                  useSubmitBehavior: true,
                  text: t("documentSendRequests.realizePopup.submit"),
                  type: "default",
                } as IButtonOptions
              }
            />
          </Form>
        </form>
      </div>
    </Popup>
  );
}
